/// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 useUserTimezone — single source of truth for "what timezone do
/// we render dates in?"
///
/// Resolution order (highest priority first):
///   1. `users/{uid}.preferences.timezone` — explicit user preference
///      set on the settings page
///   2. Browser-detected via `Intl.DateTimeFormat().resolvedOptions().timeZone`
///   3. `'UTC'` as a final fallback (covers SSR, browser without
///      `Intl`, or some sandbox-mode environments)
///
/// All timestamps in the app are stored as UTC ISO strings on the wire.
/// This composable controls how they're DISPLAYED — the actual data
/// never gets mutated to a local zone.
///
/// Components that need to render a date should use `formatInUserTz`
/// rather than calling `toLocaleString` directly so the user's
/// preference is honored consistently. Existing free-form
/// `toLocaleString` calls fall back to the browser default — they're
/// not broken, just inconsistent across the app.
/// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/// Browser timezone, computed once at module load. Cached because
/// `Intl.DateTimeFormat` calls aren't free and the answer doesn't
/// change within a session (the OS would have to change zones AND
/// the user would have to refresh).
let cachedBrowserTz: string | null = null

export function detectBrowserTimezone(): string {
  if (cachedBrowserTz) return cachedBrowserTz
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz && typeof tz === 'string') {
      cachedBrowserTz = tz
      return tz
    }
  }
  catch {
    // Older browsers / restrictive sandboxes can throw on the Intl
    // call. Fall through to the UTC default.
  }
  cachedBrowserTz = 'UTC'
  return 'UTC'
}

export function useUserTimezone() {
  const globalsStore = useGlobalsStore()
  const { userTimezone, userProfile, userId } = storeToRefs(globalsStore)

  /// Effective timezone — what we should actually use for rendering.
  /// Prefers the user-doc value; falls back to browser-detected; UTC
  /// as last resort. Reactive to userTimezone changes (saves on the
  /// settings page propagate immediately).
  const effectiveTimezone = computed<string>(() => {
    const fromStore = userTimezone.value
    if (fromStore && typeof fromStore === 'string' && fromStore.length > 0) {
      return fromStore
    }
    return detectBrowserTimezone()
  })

  /// Returns the [abbreviation, offset] pair for the effective
  /// timezone right now. Use in tooltips / labels so the user knows
  /// what zone is being applied. Re-computed on demand because DST
  /// transitions and the underlying Date both shift the offset.
  function timezoneAbbr(at: Date = new Date()): { abbr: string; offset: string } {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: effectiveTimezone.value,
        timeZoneName: 'short',
      })
      const parts = formatter.formatToParts(at)
      const abbr = parts.find((p) => p.type === 'timeZoneName')?.value || effectiveTimezone.value

      const utcMs = new Date(at.toLocaleString('en-US', { timeZone: 'UTC' })).getTime()
      const tgtMs = new Date(at.toLocaleString('en-US', { timeZone: effectiveTimezone.value })).getTime()
      const offsetMin = (tgtMs - utcMs) / (1000 * 60)
      const sign = offsetMin >= 0 ? '+' : '-'
      const h = Math.floor(Math.abs(offsetMin) / 60).toString().padStart(2, '0')
      const m = (Math.abs(offsetMin) % 60).toString().padStart(2, '0')
      return { abbr, offset: `${sign}${h}:${m}` }
    }
    catch {
      return { abbr: 'UTC', offset: '+00:00' }
    }
  }

  /// Short human-readable timestamp in the user's timezone.
  /// Example: "May 6, 2026, 2:30 PM MDT". Pass `withSeconds=true` for
  /// log-style output. Returns an empty string for null/undefined so
  /// callers don't have to wrap.
  function formatInUserTz(
    date: Date | string | null | undefined,
    opts: { withSeconds?: boolean; dateOnly?: boolean } = {},
  ): string {
    if (!date) return ''
    const d = date instanceof Date ? date : new Date(date)
    if (Number.isNaN(d.getTime())) return ''
    const fmt: Intl.DateTimeFormatOptions = {
      timeZone: effectiveTimezone.value,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZoneName: 'short',
    }
    if (!opts.dateOnly) {
      fmt.hour = 'numeric'
      fmt.minute = '2-digit'
      if (opts.withSeconds) fmt.second = '2-digit'
    }
    try {
      return new Intl.DateTimeFormat(undefined, fmt).format(d)
    }
    catch {
      return d.toISOString()
    }
  }

  /// Persist a timezone preference to Firestore. Pass an empty string
  /// to clear the override (the resolver falls back to browser
  /// detection on the next read).
  async function setUserTimezone(tz: string): Promise<void> {
    if (!userId.value) return
    await useSetDoc('users', userId.value, {
      preferences: {
        timezone: tz || null,
        timezoneSetAt: new Date(),
      },
      modifiedAt: new Date(),
    })
  }

  /// True iff the user has explicitly set a timezone (vs falling
  /// through to browser auto-detect). The settings page uses this to
  /// label "auto-detected" vs "set by you" so the user knows whether
  /// their pick is sticky.
  const isExplicitlySet = computed<boolean>(() => {
    const prefs = (userProfile.value as { preferences?: { timezone?: string } } | null | undefined)?.preferences
    return Boolean(prefs?.timezone)
  })

  return {
    effectiveTimezone,
    detectBrowserTimezone,
    timezoneAbbr,
    formatInUserTz,
    setUserTimezone,
    isExplicitlySet,
  }
}
